import * as React from "react"
import { Check, ChevronDown, X } from "lucide-react"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"

export interface ComboboxItem {
  id: string | number
  name: string
}

export interface ComboboxProps {
  value: { id: string | number | null; name: string | null } | null | undefined
  items: ComboboxItem[]
  placeholder?: string
  disabled?: boolean
  /** Подсветка «требует подтверждения» — жёлтая рамка/фон. */
  warn?: boolean
  onSelect: (item: ComboboxItem | null) => void
  className?: string
}

/**
 * Combobox поверх radix Popover + cmdk (shadcn pattern).
 * Value shape `{id, name}` matches visit-card / Oracle dictionary items.
 */
export function Combobox({
  value,
  items,
  placeholder = "Выберите…",
  disabled = false,
  warn = false,
  onSelect,
  className,
}: ComboboxProps) {
  const safeValue = value ?? { id: null, name: null }
  const [open, setOpen] = React.useState(false)

  const selectedLabel = safeValue.name ?? ""
  const hasValue = safeValue.id !== null || selectedLabel !== ""

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            "flex h-9 w-full items-center gap-1 rounded-m border bg-neutral-background-100 px-2 text-left text-body2 transition-colors hover:bg-neutral-background-100",
            "focus:outline-none focus:border-primary-500",
            warn
              ? "border-state-warning bg-state-warning/5"
              : "border-neutral-border-300 hover:border-neutral-text-400",
            disabled && "opacity-50 cursor-not-allowed",
            className,
          )}
        >
          <span
            className={cn(
              "flex-1 truncate",
              !hasValue && "text-neutral-text-400",
            )}
            title={selectedLabel || placeholder}
          >
            {selectedLabel || placeholder}
          </span>
          {hasValue && !disabled && (
            <span
              role="button"
              tabIndex={-1}
              onClick={(e) => {
                e.stopPropagation()
                onSelect(null)
              }}
              className="text-neutral-text-400 hover:text-neutral-text-800"
              aria-label="Очистить"
            >
              <X size={14} />
            </span>
          )}
          <ChevronDown
            size={14}
            className={cn(
              "shrink-0 text-neutral-text-500 transition-transform",
              open && "rotate-180",
            )}
          />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={4}
        className="w-[var(--radix-popover-trigger-width)] min-w-60 max-w-160 p-0"
      >
        <Command
          filter={(itemValue, search) => {
            if (!search) return 1
            return itemValue.includes(search.toLowerCase()) ? 1 : 0
          }}
        >
          <CommandInput placeholder="Поиск…" />
          <CommandList>
            <CommandEmpty>Ничего не найдено</CommandEmpty>
            {items.map((it) => {
              if (it == null || it.id == null) return null
              const itemName = it.name ?? ""
              const isSelected =
                safeValue.id !== null && String(safeValue.id) === String(it.id)
              return (
                <CommandItem
                  key={String(it.id)}
                  value={itemName}
                  onSelect={() => {
                    onSelect(it)
                    setOpen(false)
                  }}
                >
                  <Check
                    size={14}
                    className={cn(
                      "mr-2 shrink-0",
                      isSelected ? "opacity-100" : "opacity-0",
                    )}
                  />
                  <span className="whitespace-normal break-words">{itemName}</span>
                </CommandItem>
              )
            })}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

/** @deprecated Prefer `Combobox` from `@/components/ui/combobox`. */
export const DropdownCombobox = Combobox
export type DropdownComboboxProps = ComboboxProps
